import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { InviteTodoListMemberResponse, TodoList } from '@dreamai/types';
import { ReactComponent as CheckIcon } from '../../../icons/check.svg';
import { ReactComponent as CloseIcon } from '../../../icons/close-x-small.svg';
import { ReactComponent as EditIcon } from '../../../icons/edit.svg';
import { ReactComponent as UserPlusIcon } from '../../../icons/user-plus.svg';
import { ReactComponent as WhatsappIcon } from '../../../icons/whatsapp-color.svg';
import { useMobileNav } from '../../../contexts/MobileNavContext';
import BottomSheet from '../../common/BottomSheet';
import MemberAvatar from './MemberAvatar';
import './project-lists.scss';

interface ListMembersPanelProps {
  list: TodoList;
  currentUserId: string;
  onClose: () => void;
  /** Omitted now that lists are private: hides inviting. */
  onInvite?: (
    recipient: string,
    name?: string,
  ) => Promise<InviteTodoListMemberResponse>;
  onRemove: (userId: string) => Promise<void>;
  onRename: (name: string) => Promise<void>;
  onArchive: () => Promise<void>;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[\d\s().-]{7,}$/;

export const looksLikeRecipient = (value: string): boolean =>
  EMAIL_RE.test(value.trim()) || PHONE_RE.test(value.trim());

interface InviteNotice {
  tone: 'ok' | 'warning';
  text: string;
}

/**
 * `isGuest` and `guestInvite` land in @dreamai/types with the guest-access
 * release. `guestInvite` is the delivery state of the guest's link.
 */
type MemberWithGuest = TodoList['members'][number] & {
  isGuest?: boolean;
  guestInvite?: {
    status: 'sent' | 'send_failed';
    failureReason?: 'not_on_whatsapp' | 'undelivered';
  };
};

/**
 * `added` (existing Dream user on WhatsApp, added outright) lands in
 * @dreamai/types with the guest-access release.
 */
type InviteOutcome = InviteTodoListMemberResponse['outcome'] | 'added';

const NOT_ON_WHATSAPP_TEXT =
  "This person doesn't have WhatsApp. Invite them by email";
const INVITE_UNDELIVERED_TEXT = "Invitation didn't arrive. Invite them again";

/** Why a guest's invitation link never reached them, if it didn't. */
const guestInviteProblem = (m: MemberWithGuest): string | null => {
  if (m.guestInvite?.status !== 'send_failed') return null;
  return m.guestInvite.failureReason === 'not_on_whatsapp'
    ? NOT_ON_WHATSAPP_TEXT
    : INVITE_UNDELIVERED_TEXT;
};

const inviteNotice = (
  result: InviteTodoListMemberResponse,
  recipient: string,
  name: string,
): InviteNotice => {
  const trimmedName = name.trim();
  const who = trimmedName ? `${trimmedName} (${recipient})` : recipient;
  switch (result.outcome as InviteOutcome) {
    case 'added':
      return { tone: 'ok', text: `${who} has been added to this list` };
    case 'invited':
    case 'dream_invite_sent':
      return { tone: 'ok', text: `Invite sent to ${who}` };
    case 'already_member':
      return { tone: 'ok', text: `${who} is already on this list` };
    case 'already_invited':
    case 'dream_invite_pending':
      return { tone: 'ok', text: `${who} already has an invite` };
    case 'dream_invite_failed':
      return {
        tone: 'warning',
        text: `Couldn't send the invite to ${who} — try again`,
      };
    default:
      return { tone: 'ok', text: `Invite sent to ${who}` };
  }
};

const memberMeta = (m: MemberWithGuest): string => {
  if (m.role === 'owner') return 'Owner';
  if (m.status === 'pending') return 'Invited';
  if (m.status === 'declined') return 'Declined';
  if (m.isGuest) return 'Guest';
  return 'Member';
};

const INVALID_RECIPIENT_HINT =
  'Enter an email address or a phone number with country code, e.g. +44 7700 900123.';

/**
 * Modal for managing a shared todo list: see who's on it (and whether they've
 * accepted yet), invite people by email or phone (non-Dream users join as
 * guests via a direct link), remove members / leave, and (owner
 * only) rename or archive the list. On mobile it is a bottom sheet (Figma
 * 5227:17158): the pencil in the header swaps the title for an inline rename
 * field, and inviting is two-step — an "Invite someone" button that expands
 * into the stacked invite form (5227:17297) — so the sheet stays short.
 * Confirmations surface as a toast pill above the sheet (5236:18142).
 */
const ListMembersPanel: React.FC<ListMembersPanelProps> = ({
  list,
  currentUserId,
  onClose,
  onInvite,
  onRemove,
  onRename,
  onArchive,
}) => {
  const { isMobile } = useMobileNav();
  const isOwner = list.myRole === 'owner';
  const [recipient, setRecipient] = useState('');
  const [inviteeName, setInviteeName] = useState('');
  const [notice, setNotice] = useState<InviteNotice | null>(null);
  const recipientInvalid =
    recipient.trim().length > 0 && !looksLikeRecipient(recipient);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(list.name);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isMobile) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, isMobile]);

  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    },
    [],
  );

  const showToast = (text: string) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(text);
    const ms = text.length > 60 ? 8000 : 4000;
    toastTimer.current = setTimeout(() => setToast(null), ms);
  };

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (err) {
      const maybe = err as { response?: { data?: { error?: string } } };
      setError(maybe?.response?.data?.error || 'Something went wrong');
    } finally {
      setBusy(null);
    }
  };

  const members = [...list.members].sort((a, b) => {
    if (a.role !== b.role) return a.role === 'owner' ? -1 : 1;
    const order = { accepted: 0, pending: 1, declined: 2 };
    if (a.status !== b.status) return order[a.status] - order[b.status];
    return a.name.localeCompare(b.name);
  });

  const ownerLabel =
    list.ownerUserId === currentUserId
      ? isMobile
        ? `${list.ownerName} (You)`
        : 'you'
      : list.ownerName;

  const submitInvite = (e: React.FormEvent) => {
    e.preventDefault();
    const target = recipient.trim();
    if (!onInvite || !looksLikeRecipient(target)) return;
    run('invite', async () => {
      const result = await onInvite(target, inviteeName);
      const next = inviteNotice(result, target, inviteeName);
      setRecipient('');
      setInviteeName('');
      if (isMobile && next.tone === 'ok') {
        setInviting(false);
        showToast(next.text);
        return;
      }
      setNotice(next);
    });
  };

  const cancelRename = () => {
    setRenaming(false);
    setName(list.name);
  };

  const submitRename = (e: React.FormEvent) => {
    e.preventDefault();
    const next = name.trim();
    if (!next) return;
    run('rename', async () => {
      await onRename(next);
      setRenaming(false);
      if (isMobile) showToast(`Updated list to ${next}`);
    });
  };

  const inviteHint: { className: string; text: string; alert: boolean } | null =
    recipientInvalid
      ? { className: 'warning', text: INVALID_RECIPIENT_HINT, alert: true }
      : notice
        ? { className: notice.tone, text: notice.text, alert: false }
        : error
          ? { className: 'warning', text: error, alert: true }
          : null;

  const renderInviteHint = (className: string) =>
    inviteHint ? (
      <p
        className={`${className} ${inviteHint.className}`}
        role={inviteHint.alert ? 'alert' : 'status'}
      >
        {inviteHint.text}
      </p>
    ) : (
      <p className={`${className} list-invite-whatsapp-hint`}>
        If they&rsquo;re not on Dream yet they&rsquo;ll get an invitation by{' '}
        <span className='list-invite-whatsapp-word'>
          <WhatsappIcon aria-hidden />
          WhatsApp
        </span>{' '}
        or email
      </p>
    );

  const renameForm = (
    <form className='list-rename-form' onSubmit={submitRename}>
      <input
        autoFocus
        className='list-rename-input'
        aria-label='List name'
        value={name}
        maxLength={120}
        onChange={(e) => setName(e.target.value)}
      />
      <button
        type='submit'
        className='list-members-btn primary'
        disabled={!name.trim() || busy === 'rename'}
      >
        Save
      </button>
      <button type='button' className='list-members-btn' onClick={cancelRename}>
        Cancel
      </button>
    </form>
  );

  const mobileRenameForm = (
    <form className='list-rename-sheet-form' onSubmit={submitRename}>
      <input
        autoFocus
        className='bottom-sheet-input list-rename-sheet-input'
        aria-label='List name'
        value={name}
        maxLength={120}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            cancelRename();
          }
        }}
        onChange={(e) => setName(e.target.value)}
      />
      <button
        type='submit'
        className='list-rename-sheet-submit'
        aria-label='Save list name'
        disabled={!name.trim() || busy === 'rename'}
      >
        <CheckIcon />
      </button>
    </form>
  );

  const membersSection = (
    <div>
      <p className='list-members-section-label'>
        {members.length} {members.length === 1 ? 'person' : 'people'}
      </p>
      <ul className='list-members-list'>
        {members.map((m) => {
          const isMe = m.userId === currentUserId;
          const canRemove =
            m.role !== 'owner' && (isOwner || isMe) && busy !== m.userId;
          const inviteProblem = guestInviteProblem(m);
          return (
            <li
              key={m.userId}
              className={`list-member status-${m.status} role-${m.role}${inviteProblem ? ' invite-failed' : ''}`}
            >
              <MemberAvatar name={m.name} size={isMobile ? 36 : 32} />
              <div className='list-member-text'>
                <span className='list-member-name'>
                  {m.name}
                  {isMe && (
                    <span className='list-member-you'>
                      {' '}
                      ({isMobile ? 'You' : 'you'})
                    </span>
                  )}
                </span>
                <span className='list-member-meta'>{memberMeta(m)}</span>
                {inviteProblem && (
                  <span className='list-member-problem'>{inviteProblem}</span>
                )}
              </div>
              {canRemove && (
                <button
                  className='list-members-btn subtle'
                  onClick={() =>
                    run(m.userId, async () => {
                      await onRemove(m.userId);
                      if (isMe) onClose();
                    })
                  }
                >
                  {isMe
                    ? 'Leave'
                    : m.status === 'pending'
                      ? 'Revoke'
                      : 'Remove'}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );

  const externalSection = list.externalInvites.length > 0 && (
    <div>
      <p className='list-members-section-label'>Not on Dream yet</p>
      <ul className='list-members-list'>
        {list.externalInvites.map((inv) => (
          <li
            key={`${inv.channel}:${inv.address}`}
            className='list-member status-external'
          >
            <MemberAvatar
              name={inv.name || inv.address}
              size={isMobile ? 36 : 32}
            />
            <div className='list-member-text'>
              <span className='list-member-name'>
                {inv.name || inv.address}
              </span>
              <span className='list-member-meta'>
                {inv.name ? `${inv.address} · ` : ''}
                {inv.status === 'send_failed'
                  ? 'Invitation not sent'
                  : `Invited by ${inv.channel === 'phone' ? 'WhatsApp' : 'email'}`}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );

  const archiveSection = isOwner && (
    <div className='list-members-footer'>
      {confirmArchive ? (
        <>
          <span className='list-members-footer-text'>
            Archive this todo list and all of its tasks?
          </span>
          <button
            className='list-members-btn danger'
            disabled={busy === 'archive'}
            onClick={() =>
              run('archive', async () => {
                await onArchive();
                onClose();
              })
            }
          >
            Yes, archive
          </button>
          <button
            className='list-members-btn'
            onClick={() => setConfirmArchive(false)}
          >
            Keep
          </button>
        </>
      ) : (
        <button
          className='list-members-btn subtle danger-text'
          onClick={() => setConfirmArchive(true)}
        >
          Archive list
        </button>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <BottomSheet
        open
        onClose={onClose}
        title={list.name}
        subtitle={`Owned by ${ownerLabel}`}
        className='list-members-sheet'
        toast={toast}
        header={renaming ? mobileRenameForm : undefined}
        headerActions={
          isOwner ? (
            <button
              type='button'
              className='bottom-sheet-icon-btn'
              aria-label={renaming ? 'Cancel rename' : 'Rename list'}
              aria-pressed={renaming}
              onClick={() => (renaming ? cancelRename() : setRenaming(true))}
            >
              <EditIcon />
            </button>
          ) : undefined
        }
      >
        {membersSection}
        {externalSection}
        {!onInvite ? (
          error && (
            <p className='bottom-sheet-hint warning' role='alert'>
              {error}
            </p>
          )
        ) : inviting ? (
          <form className='bottom-sheet-form' onSubmit={submitInvite}>
            <label className='bottom-sheet-field'>
              <span className='bottom-sheet-label'>Email or phone number</span>
              <input
                className='bottom-sheet-input'
                type='text'
                inputMode='email'
                autoComplete='off'
                autoCapitalize='none'
                aria-invalid={recipientInvalid || undefined}
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
              />
            </label>
            <label className='bottom-sheet-field'>
              <span className='bottom-sheet-label'>Name (optional)</span>
              <input
                className='bottom-sheet-input'
                type='text'
                autoComplete='off'
                value={inviteeName}
                maxLength={80}
                onChange={(e) => setInviteeName(e.target.value)}
              />
            </label>
            <div className='list-invite-submit'>
              <button
                type='submit'
                className='bottom-sheet-primary'
                disabled={!looksLikeRecipient(recipient) || busy === 'invite'}
              >
                {busy === 'invite' ? 'Sending…' : 'Send invite'}
              </button>
              {renderInviteHint('bottom-sheet-hint')}
            </div>
          </form>
        ) : (
          <>
            {error && (
              <p className='bottom-sheet-hint warning' role='alert'>
                {error}
              </p>
            )}
            <button
              type='button'
              className='bottom-sheet-secondary'
              onClick={() => {
                setNotice(null);
                setError(null);
                setInviting(true);
              }}
            >
              <UserPlusIcon />
              Invite someone
            </button>
          </>
        )}
        {archiveSection}
      </BottomSheet>
    );
  }

  return createPortal(
    <div className='list-members-overlay' onMouseDown={onClose}>
      <div
        className='list-members-panel'
        role='dialog'
        aria-modal='true'
        aria-label={`${list.name} members`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className='list-members-header'>
          {renaming ? (
            renameForm
          ) : (
            <>
              <div className='list-members-header-text'>
                <h3 className='list-members-title'>{list.name}</h3>
                <p className='list-members-subtitle'>Owned by {ownerLabel}</p>
              </div>
              <div className='list-members-header-actions'>
                {isOwner && (
                  <button
                    className='list-members-btn'
                    onClick={() => setRenaming(true)}
                  >
                    Rename
                  </button>
                )}
                <button
                  className='list-members-close'
                  aria-label='Close'
                  onClick={onClose}
                >
                  <CloseIcon />
                </button>
              </div>
            </>
          )}
        </div>

        <div className='list-members-body'>
          {membersSection}
          {externalSection}
          {onInvite && (
            <form className='list-invite-form' onSubmit={submitInvite}>
              <label
                className='list-members-section-label'
                htmlFor='list-invite-recipient'
              >
                Invite someone
              </label>
              <div className='list-invite-row'>
                <input
                  id='list-invite-recipient'
                  className='list-invite-input'
                  type='text'
                  inputMode='email'
                  autoComplete='off'
                  autoCapitalize='none'
                  placeholder='Email or phone'
                  aria-invalid={recipientInvalid || undefined}
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                />
                <input
                  className='list-invite-input list-invite-name'
                  type='text'
                  autoComplete='off'
                  placeholder='Name (optional)'
                  value={inviteeName}
                  maxLength={80}
                  onChange={(e) => setInviteeName(e.target.value)}
                />
                <button
                  type='submit'
                  className='list-members-btn primary'
                  disabled={!looksLikeRecipient(recipient) || busy === 'invite'}
                >
                  {busy === 'invite' ? 'Inviting…' : 'Invite'}
                </button>
              </div>
              {renderInviteHint('list-invite-hint')}
            </form>
          )}
        </div>

        {archiveSection}
      </div>
    </div>,
    document.body,
  );
};

export default ListMembersPanel;
